import {Injectable} from '@angular/core';
import {FixedAssetModel} from '../DataModels/fixed-asset-model';
import {MacrsPeriodAmount} from '../DataModels/depreciation-models';

@Injectable({providedIn: 'root'})
export class MacrsService {
  // IRS MACRS annual rate tables (% of depreciable basis), half-year convention
  // 200% DB: 3,5,7,10-year; 150% DB: 15,20-year
  private macrsAnnualTables: Record<string, number[]> = {
    '3-year': [33.33, 44.45, 14.81, 7.41],
    '5-year': [20.00, 32.00, 19.20, 11.52, 11.52, 5.76],
    '7-year': [14.29, 24.49, 17.49, 12.49, 8.93, 8.92, 8.93, 4.46],
    '10-year': [10.00, 18.00, 14.40, 11.52, 9.22, 7.37, 6.55, 6.55, 6.56, 6.55, 3.28],
    '15-year': [5.00, 9.50, 8.55, 7.70, 6.93, 6.23, 5.90, 5.90, 5.91, 5.90, 5.91, 5.90, 5.91, 5.90, 5.91, 2.95],
    // Rounded to 3 decimals where IRS publishes more precision
    '20-year': [3.75, 7.219, 6.677, 6.177, 5.713, 5.285, 4.888, 4.522, 4.462, 4.461, 4.462, 4.461, 4.462, 4.461, 4.462, 4.461, 4.462, 4.461, 4.462, 4.461, 2.231],
  };

  // MACRS categories mapped to useful life in months (for convenience)
  private macrsMonthsMap: Record<string, number> = {
    '3-year': 36,
    '5-year': 60,
    '7-year': 84,
    '10-year': 120,
    '15-year': 180,
    '20-year': 240,
    '27.5-year': 330, // residential rental real property
    '39-year': 468,   // nonresidential real property
  };

  /** Normalize various user-entered MACRS category labels to our canonical keys.
   *  Examples:
   *   - "5 Year", "5-year", "5yr", " 5 - YEAR " => "5-year"
   *   - "27.5 year", "27.5-Year" => "27.5-year"
   */
  private normalizeCategory(label?: string): string | null {
    if (!label) {
      return null;
    }
    const s = String(label).trim();
    if (!s) {
      return null;
    }
    // Try direct match first
    if (this.macrsMonthsMap[s as keyof typeof this.macrsMonthsMap]) {
      return s as keyof typeof this.macrsMonthsMap;
    }
    // Extract number (supports decimal) and ensure it ends with -year
    const m = s.replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
    if (!m) {
      return null;
    }
    const num = m[1];
    // Canonical key
    const key = `${num}-year`;
    return this.macrsMonthsMap[key as keyof typeof this.macrsMonthsMap] ? key : null;
  }

  getMacrsMonths(category?: string): number | null {
    const norm = this.normalizeCategory(category);
    if (!norm) {
      return null;
    }
    const months = this.macrsMonthsMap[norm];
    return months && months > 0 ? months : null;
  }

  macrsDepForPeriod(asset: FixedAssetModel, periodStartIso?: string, periodEndIso?: string): number | null {
    // console.log('macrsDepForPeriod', asset, periodStartIso, periodEndIso);
    if (!asset) {
      return null;
    }
    const category = this.normalizeCategory(asset.category || '');
    const basis = this.getDepreciableBasis(asset);
    if (basis <= 0) {
      return 0;
    }
    const inSvc = this.parseIsoDate(asset.inServiceDate || asset.acquisitionDate);
    const pStart = this.parseIsoDate(periodStartIso || '');
    if (!inSvc || !pStart) {
      return null;
    }

    // Real property straight-line mid-month
    if (category === '27.5-year' || category === '39-year') {
      const years = category === '27.5-year' ? 27.5 : 39;
      const totalMonths = Math.round(years * 12);
      const monthlySL = basis / totalMonths;
      const mSince = this.monthsDiff(this.startOfMonth(inSvc), this.startOfMonth(pStart));
      if (mSince < 0 || mSince >= totalMonths) {
        return 0;
      }
      if (mSince === 0 || mSince === totalMonths - 1) {
        return +(monthlySL / 2).toFixed(2);
      }
      return +monthlySL.toFixed(2);
    }

    // Personal property: half-year tables, spread by month
    const table = category ? this.macrsAnnualTables[category] : undefined;
    if (!table) {
      return null;
    }
    // Months since in-service (UTC-safe)
    let mSince = this.monthsDiff(this.startOfMonth(inSvc), this.startOfMonth(pStart));
    if (mSince < 0) {
      // Clamp negative diffs to 0 to avoid spurious zeros due to parsing or boundary skew
      mSince = 0;
    }
    // Determine MACRS year index (0-based) and clamp within available table years
    let yearIndex = Math.floor(mSince / 12);
    if (yearIndex < 0) {
      yearIndex = 0;
    }
    if (yearIndex >= table.length) {
      yearIndex = table.length - 1;
    }
    const annualRatePct = table[yearIndex] || 0;
    const annualAmt = basis * (annualRatePct / 100);
    const monthlyAmt = annualAmt / 12;
    return +monthlyAmt.toFixed(2);
  }

  private parseIsoDate(d?: string | Date): Date | null {
    if (!d) {
      return null;
    }
    // If already a Date, validate and return
    if (d instanceof Date) {
      return isNaN(d.getTime()) ? null : d;
    }
    // Accept common date-like strings (ISO and others parseable by Date)
    const s = String(d).trim();
    if (!s) {
      return null;
    }
    const dt = new Date(s);
    return isNaN(dt.getTime()) ? null : dt;
  }

  private startOfMonth(dt: Date): Date {
    // Use UTC to avoid local timezone shifting to a prior/next month
    return new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), 1));
  }

  private monthsDiff(a: Date, b: Date): number {
    const am = a.getUTCFullYear() * 12 + a.getUTCMonth();
    const bm = b.getUTCFullYear() * 12 + b.getUTCMonth();
    return bm - am;
  }

  private getDepreciableBasis(asset: FixedAssetModel): number {
    const cost = Number(asset?.acquisitionCost || 0);
    const salvage = Number(asset?.salvageValue || 0);
    return Math.max(0, cost - salvage);
  }

  /** Build a YYYY-MM key from a date-like value without timezone skew.
   * - If input is a string 'YYYY-MM' or 'YYYY-MM-DD', return first 7 chars.
   * - Else, use UTC getters to avoid local TZ shifting month.
   */
  private ymKey(d: Date | string): string {
    if (typeof d === 'string') {
      const s = d.trim();
      // Basic ISO-like formats handled by substring to avoid Date parsing
      if (/^\d{4}-\d{2}(-\d{2})?$/.test(s)) {
        return s.substring(0, 7);
      }
      // Fallback to Date parse if unknown string
      const dt = new Date(s);
      if (!isNaN(dt.getTime())) {
        const y = dt.getUTCFullYear();
        const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
        return `${y}-${m}`;
      }
      return '';
    }
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }

  /** Compute monthly MACRS amounts for a date range (inclusive), returning one entry per calendar month. */
  computeMacrsForRange(asset: FixedAssetModel, fromDate: string | Date, toDate: string | Date): MacrsPeriodAmount[] {
    const start = this.parseIsoDate(fromDate);
    const end = this.parseIsoDate(toDate);
    if (!asset || !start || !end) {
      return [];
    }
    const s0 = this.startOfMonth(start);
    const e0 = this.startOfMonth(end);
    const out: MacrsPeriodAmount[] = [];
    // Use a pure UTC month counter to avoid DST/local shifts
    const sYear = s0.getUTCFullYear();
    const sMonth = s0.getUTCMonth();
    const months = this.monthsDiff(s0, e0); // inclusive span in months
    for (let i = 0; i <= months; i++) {
      const ymYear = sYear + Math.floor((sMonth + i) / 12);
      const ymMonth = (sMonth + i) % 12;
      const periodStart = new Date(Date.UTC(ymYear, ymMonth, 1));
      // Last day of month in UTC: day 0 of next month
      const periodEnd = new Date(Date.UTC(ymYear, ymMonth + 1, 0));
      const amt = this.macrsDepForPeriod(asset, periodStart.toISOString(), periodEnd.toISOString());
      // year index meta for personal property tables
      const inSvc = this.parseIsoDate(asset.inServiceDate || asset.acquisitionDate);
      let yearIndex: number | undefined = undefined;
      if (inSvc) {
        const mSince = this.monthsDiff(this.startOfMonth(inSvc), periodStart);
        yearIndex = Math.floor(Math.max(0, mSince) / 12);
      }
      out.push({
        periodStart: periodStart.toISOString(),
        periodEnd: periodEnd.toISOString(),
        amount: amt,
        meta: {yearIndex},
      });
    }
    return out;
  }

  /** Convenience: return a map keyed by YYYY-MM to MACRS amount for fast lookups in templates */
  getMacrsMapByMonth(asset: FixedAssetModel, fromDate: string | Date, toDate: string | Date): Record<string, number | null> {
    const arr = this.computeMacrsForRange(asset, fromDate, toDate);
    const map: Record<string, number | null> = {};
    for (const p of arr) {
      const k = this.ymKey(p.periodStart);
      map[k] = p.amount;
    }
    return map;
  }
}
